/**
 * requestBudget — the client side of the API's UTF-8 byte budget on the two
 * Combine request bodies (`schemas.py`'s `_MAX_COMBINE_BODY_BYTES`,
 * /pr-review iter 2, Agent A §6).
 *
 * The API's caps count CHARACTERS (10 values x 60 per clause, 100 unticks);
 * the edge's WAF counts BYTES and refuses a body over 8192 with a bare 403
 * that carries no CORS headers. A max-shaped body of accented, quoted or CJK
 * values is 8.5-12 KB. The API refuses (422) a request whose RAW body is
 * over 7900 bytes; this module lets the page say so BEFORE sending. Both
 * sides count the same bytes (/pr-review iter 3): the API the body it
 * received, this module the UTF-8 length of `JSON.stringify(body)`, which
 * is exactly what `imports.client.ts`'s `request()` sends. So a body this
 * module lets through is never refused by the API for its size.
 */
import { ImportsApiError } from "../../../clients/imports.client";

/** The API's `_MAX_COMBINE_BODY_BYTES`. */
export const MAX_COMBINE_BODY_BYTES = 7900;

export const REQUEST_TOO_LARGE = "Request too large — remove some filter values.";

/** Stands in for `POST /combine`'s `request_id` when the page measures the
 *  Generate body before the id exists (`useCombineGenerate` mints it on
 *  submit, task 2026-10-06-combine-generate-idempotent): the same 36 characters as
 *  `crypto.randomUUID()`, so the measured bytes are the bytes sent. */
export const REQUEST_ID_PLACEHOLDER = "00000000-0000-4000-8000-000000000000";

/** UTF-8 byte length of `JSON.stringify(body)` — what `fetch` sends. */
export function jsonByteLength(body: unknown): number {
  return new TextEncoder().encode(JSON.stringify(body)).length;
}

/** Whether `body` is over the budget — the API's own test on the same bytes. */
export function exceedsBodyBudget(body: unknown, max: number = MAX_COMBINE_BODY_BYTES): boolean {
  return jsonByteLength(body) > max;
}

/** A 403 with no JSON detail: the edge (WAF) refused the body, not the API
 *  (every API 403 carries a `detail`). Only readable when the response has
 *  CORS headers; otherwise `fetch` rejects with a network error instead. */
export function isEdgeRejection(err: unknown): boolean {
  return (
    err instanceof ImportsApiError &&
    err.status === 403 &&
    err.detail === undefined &&
    err.validationMessages === undefined &&
    err.detailData === undefined
  );
}
